import * as Yup from 'yup';
import dayjs from 'dayjs';
import { CollapseSection } from '@/components/ats';
import { DEFAULT_ATTACHMENT_FILES } from '@/constants/attachment-files';

const collapseSectionProps = () => ({
  headerConfig: {
    containerStyles: () => ({
      backgroundColor: 'ats-tertiary',
      p: '15px',
    }),
    textStyles: {
      color: 'ats-primary',
    },
    iconStyles: {
      width: '24px',
      height: '24px',
    },
  },
  styles: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '6px',
  },
  collapseStyles: {
    display: 'flex',
    flexDirection: 'column',
    padding: '1em 2em',
    backgroundColor: 'white',
  },
});

const MAX_ALLOWED_NUMBER = 2 ** 49;

export const getFieldConfig = (
  field,
  isDisabled,
  styleVersion = 'v1',
  labelStyles = {
    fontSize: '14px',
    fontWeight: '650',
  }
) => {
  const {
    id,
    type, // from static fields
    kind, // from custom fields
    multi,
    label,
    options, // labels for enum boolean
    required,
    values = [],
    placeholder,
    description,
  } = field || {};

  const fieldMap = {
    string: {
      type: styleVersion === 'v2' ? 'kInput' : 'input',
      maxLength: 255,
      placeholder,
      disabled: isDisabled,
      labelStyles,
      subLabel: description,
    },
    email: {
      type: styleVersion === 'v2' ? 'kInput' : 'input',
      maxLength: 255,
      placeholder,
      disabled: isDisabled,
      optionalValidation: !required,
      validation: Yup.string()
        .email('Valid email address required (if provided)')
        .matches(
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          'Valid email address required (if provided)'
        )
        .when([], {
          is: () => required,
          then: (schema) => schema.required('Email is required'),
        }),
      labelStyles,
    },
    rel_tag: {
      type: styleVersion === 'v2' ? 'kSelect' : 'select',
      disabled: isDisabled,
      isMulti: multi,
      placeholder,
      options: values
        // sometimes we get deleted filter options based on tha
        ?.filter((opt) => opt?.deleted !== true || opt?.deleted === undefined)
        ?.map((opt) => ({ label: opt?.value, value: opt?.id })),
      labelStyles,
      subLabel: description,
    },
    text: {
      type: 'textArea',
      disabled: isDisabled,
      isRequired: required,
      placeholder,
      validation: Yup.string()
        .trim()
        .max(10000, 'Max 10,000 characters allowed')
        .when([], {
          is: () => required,
          then: (schema) => schema.required(`${label} is required`),
        }),
      styleVersion: styleVersion,
      labelStyles,
      subLabel: description,
    },
    number: {
      type: styleVersion === 'v2' ? 'kInput' : 'input',
      inputType: 'number',
      fieldType: 'number',
      disabled: isDisabled,
      placeholder,
      validation: Yup.string()
        .test('number', 'Please enter a valid number', (value) => {
          if (!value) return true;
          return !isNaN(Number(value));
        })
        .test('float', 'Max 4 decimal places allowed', (value) => {
          if (!value) return true;
          if (value?.includes('.')) {
            const [, decimal = ''] = value?.split('.') ?? [];
            return decimal.length <= 4;
          }
          return true;
        })
        .test('length', 'Max limit reached', (value) => {
          if (!value) return true;
          const numberValue = Number(value);
          return numberValue <= MAX_ALLOWED_NUMBER;
        })
        .when([], {
          is: () => required,
          then: (schema) => schema.required(`${label} is required`),
        }),
      labelStyles,
      subLabel: description,
    },
    phone: {
      type: styleVersion === 'v2' ? 'kInput' : 'input',
      maxLength: 255,
      disabled: isDisabled,
      placeholder,
      labelStyles,
      subLabel: description,
    },
    date: {
      type: 'date',
      disabled: isDisabled,
      placeholder,
      dateFormat: (date) => dayjs(date).valueOf(),
      isRequired: required,
      styleVersion: styleVersion,
      sendDateInUTC: true,
      labelStyles,
      subLabel: description,
    },
    enum_boolean: {
      type: 'radio',
      disabled: isDisabled,
      options: options?.labels
        ? [
            {
              label: options?.labels?.Y,
              value: 'Y',
            },
            {
              label: options?.labels?.N,
              value: 'N',
            },
          ]
        : [
            {
              label: 'Yes',
              value: 'Y',
            },
            {
              label: 'No',
              value: 'N',
            },
          ],
      labelStyles,
      subLabel: description,
      placeholder,
    },
    url: {
      type: styleVersion === 'v2' ? 'kInput' : 'input',
      disabled: isDisabled,
      optionalValidation: required,
      maxLength: 250,
      validation: Yup.string()
        .matches(
          /^(https?:\/\/)?([\w-]+(\.[\w-]+)*\.[\w-]{2,})(:\d+)?(\/\S*)?$/,
          'Please enter a valid URL'
        )
        .when([], {
          is: () => required,
          then: (schema) => schema.required('URL is required'),
        })
        .trim()
        .max(250, 'Max 250 characters allowed'),

      isRequired: required,
      labelStyles,
      subLabel: description,
      placeholder,
    },
    currency: {
      disabled: isDisabled,
      type: styleVersion === 'v2' ? 'kInput' : 'input',
      placeholder,
      formatType: 'currency',
      labelStyles,
      subLabel: description,
    },
    richtext: {
      type: 'editor',
      disabled: isDisabled,
      placeholder,
      isRequired: required,
      pluginsConfig: ['table'],
      printFormat: true,
      containerStyles: {
        width: '100%',
      },
      editorStyles: {
        height: '300px',
      },
      toolBarProps: {
        containerProps: {
          paddingTop: '0 !important',
        },
      },
      subLabel: description,
    },
    rel_file: (() => {
      let acceptedFiles = DEFAULT_ATTACHMENT_FILES.join(',');
      let description = undefined;

      switch (id) {
        case 'photo':
          acceptedFiles = '.jpeg,.png';
          description = 'Supported : .jpeg, .png';
          break;
        case 'resume':
        case 'cover_letter':
          acceptedFiles = '.pdf,.docx,.doc';
          description = 'Supported : .pdf, .docx, .doc';
          break;
        default:
          break;
      }

      return {
        disabled: isDisabled,
        placeholder,
        type: 'upload',
        acceptedFiles,
        description: description,
        errorMessage: `${label} is required`,
        labelStyles,
        subLabel: description,
      };
    })(),
  };

  return fieldMap?.[type || kind] || {};
};

export const generateCustomFormConfig = (
  config,
  splitName = false,
  isDisabled = false,
  tokenPrefix = '',
  styleVersion = 'v1',
  labelStyles = {
    fontSize: '14px',
    fontWeight: '650',
  }
) => {
  const {
    ats_custom_fields = [],
    field_groups = {},
    static_fields = [],
  } = config || {};

  const formConfig = {
    static: [],
    custom: [],
  };

  //static fields
  static_fields?.forEach((section, index) => {
    formConfig.static.push({
      id: section?.id,
      type: 'wrapper',
      fields: [],
      Wrapper: CollapseSection,
      wrapperProps: {
        title: section?.label,
        isHeaderClickable: true,
        defaultIsOpen: true,
        ...collapseSectionProps(),
      },
    });

    const currentItem = formConfig.static?.[index]?.fields;

    section?.fields?.forEach((field) => {
      const { hidden, required, id, label, type } = field;
      const otherItemsBasedOnType =
        getFieldConfig(field, isDisabled, styleVersion, labelStyles) || {};

      //handle complex type
      if (type === 'complex') {
        //need to handle complex data
      }

      // name
      else if (id === 'name' && splitName) {
        currentItem.push({
          name: `static_first_name`,
          label: 'First name',
          maxLength: 255,
          isRequired: required,
          ...otherItemsBasedOnType,
        });
        currentItem.push({
          name: `static_last_name`,
          label: 'Last name',
          maxLength: 255,
          isRequired: required,
          ...otherItemsBasedOnType,
        });
      } else if (!hidden) {
        currentItem.push({
          name: `static_${id}`,
          label,
          isRequired: required,
          ...otherItemsBasedOnType,
        });
      }
    });
  });

  //custom fields

  Object?.entries(field_groups).forEach(([key, value]) => {
    formConfig.custom.push({
      id: key,
      type: 'wrapper',
      fields: [],
      Wrapper: CollapseSection,
      wrapperProps: {
        title: value,
        isHeaderClickable: true,
        defaultIsOpen: true,
        ...collapseSectionProps(),
      },
    });
  });

  ats_custom_fields?.forEach((field) => {
    const { hidden, required, id, label, field_group_idx, token = '' } = field;
    const otherItemsBasedOnType =
      getFieldConfig(field, isDisabled, styleVersion, labelStyles) || {};

    let currentItem;
    if (field_group_idx in field_groups) {
      currentItem = formConfig.custom?.[field_group_idx - 1]?.fields;
    } else {
      currentItem = formConfig.custom;
    }

    if (!hidden) {
      currentItem.push({
        name: `custom_${id}`,
        label,
        isRequired: required,
        tokenName: `${tokenPrefix}.${token}`,
        ...otherItemsBasedOnType,
      });
    }
  });

  return formConfig;
};
